import { useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { AUTH_CAPTCHA_CHALLENGE_ID } from "testIds";
import { loadCaptchaScript } from "utils/captchaScript";

type TurnstileOptions = {
  sitekey: string;
  action: string;
  theme: "dark" | "light";
  size: "flexible";
  appearance: "interaction-only";
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TurnstileChallenge = ({
  sitekey,
  onSuccess,
  onError,
}: {
  sitekey: string;
  onSuccess: (token: string) => void;
  onError: () => void;
}) => {
  const container = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useTheme();
  const callbacks = useRef({ onSuccess, onError });
  callbacks.current = { onSuccess, onError };

  useEffect(() => {
    let active = true;
    let widgetId: string | undefined;
    let api: TurnstileApi | undefined;
    const timeout = window.setTimeout(() => {
      if (active) callbacks.current.onError();
    }, 60000);
    const fail = () => {
      if (active) callbacks.current.onError();
    };
    loadCaptchaScript(TURNSTILE_SCRIPT)
      .then(() => {
        if (!active || !container.current) return;
        api = window.turnstile;
        if (!api) throw new Error("Captcha is unavailable");
        widgetId = api.render(container.current, {
          sitekey,
          action: "authenticate",
          theme: isDarkMode ? "dark" : "light",
          size: "flexible",
          appearance: "interaction-only",
          callback: (token) => {
            if (active) callbacks.current.onSuccess(token);
          },
          "error-callback": fail,
          "expired-callback": fail,
          "timeout-callback": fail,
        });
      })
      .catch(fail);
    return () => {
      active = false;
      window.clearTimeout(timeout);
      if (widgetId !== undefined) api?.remove(widgetId);
    };
  }, [sitekey, isDarkMode]);

  return <div ref={container} data-testid={AUTH_CAPTCHA_CHALLENGE_ID} />;
};

export default TurnstileChallenge;

const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
